/**
 * AppKitProvider.jsx — Provider Wagmi + AppKit (reown) pour l'application.
 *
 * Modif EarniConnect (SPEC §8.2) :
 *   La config wagmi inline est remplacée par l'import de
 *   `components/EarniWalletModal/config/wagmiConfig.js` (source unique).
 *   Cela garantit que les hooks wagmi natifs dans EarniWalletModal (useConnect,
 *   useSignMessage…) opèrent sur la MÊME config que AppKit.
 *
 *   WagmiAdapter reçoit notre `wagmiConfig` pré-construit via l'option `wagmiConfig`.
 *   Les chains/networks/projectId/features restent inchangés.
 */
import React from 'react';
import { createAppKit } from '@reown/appkit/react'

import { WagmiProvider } from 'wagmi'
import { avalanche, avalancheFuji } from '@reown/appkit/networks'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi'

/* Config wagmi centralisée — EarniWalletModal + AppKit partagent la même config */
import { wagmiConfig } from './components/EarniWalletModal/config/wagmiConfig';

const queryClient = new QueryClient()

const projectId = 'ae6fb9becb28842f5fee24787f4cf47e'

const metadata = {
  name: 'Earniverse',
  description: 'Earniverse',
  url: process.env.REACT_APP_URL, // origin must match your domain & subdomain
  icons: ['https://avatars.githubusercontent.com/u/179229932']
};

/* Réseau actif (mainnet / Fuji staging) — même logique que wagmiConfig.js */
const networks = [process.env.REACT_APP_PROD_ENV === 'staging' ? avalancheFuji : avalanche];

/*
 * WagmiAdapter : on passe notre wagmiConfig pré-construit.
 * AppKit utilisera ce config existant au lieu d'en créer un nouveau.
 * Si WagmiAdapter de cette version ne supporte pas l'option wagmiConfig,
 * le fallback est de créer un adapter standard — les hooks EarniWalletModal
 * resteront quand même sur notre config via WagmiProvider ci-dessous.
 */
const wagmiAdapter = new WagmiAdapter({
  wagmiConfig,     /* config EarniConnect centralisée */
  networks,
  projectId,
  ssr: true
});

/* Initialisation AppKit (reown) — paramètres inchangés */
createAppKit({
  featuredWalletIds: [
    'fd20dc426fb37566d803205b19bbc1d4096b248ac04548e3cfb6b3a38bd033aa',
    'c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96',
    '4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0',
  ],
  includeWalletIds: [
    'c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96',
    'fd20dc426fb37566d803205b19bbc1d4096b248ac04548e3cfb6b3a38bd033aa'
  ],
  excludeWalletIds: [
    '92ebfc08f0ac3bc8015a9bf843f9366750d5139b00a166086ad893aeb701acd4', //"WalletConnect"
    // '4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0' //"Trust Wallet"
  ],
  allWallets: 'HIDE',
  adapters: [wagmiAdapter],
  networks,
  projectId,
  metadata,
  enableWalletConnect: false,
  features: {
    analytics: true,
    email: true,
    socials: []
  }
})

const AppKitProvider = ({ children }) => {
  /*
   * On passe notre wagmiConfig directement au WagmiProvider.
   * wagmiAdapter.wagmiConfig et wagmiConfig sont identiques si WagmiAdapter
   * a accepté l'option ; sinon on force l'utilisation de la config centralisée.
   */
  return (
    <WagmiProvider config={wagmiConfig}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </WagmiProvider>
  )
}

export default AppKitProvider;